// STARWEAVE — procedural WebAudio sound engine. Everything is synthesised at
// runtime (oscillators / filtered noise / envelopes) — there are NO audio files.
//
//   • an evolving low atmospheric space pad/drone (always on after init)
//   • an engine hum that tracks velocity via setVelocity(kms)
//   • one-shot SFX: onWarp() (hyperdrive whoosh + sub-boom), onScan() (clean
//     ping), onUi() (soft blip)
//   • a master gain + mute toggle persisted to localStorage ('sw_mute'), also
//     driven by the window CustomEvent 'sw-toggle-mute'
//
// Browser autoplay policy forbids audio before a user gesture, so the graph is
// built lazily inside init() (call it on the first pointerdown). Every method is
// a safe no-op until init() has run, so callers never need to guard.

type AnyAC = typeof AudioContext;

const MUTE_KEY = 'sw_mute';

function readMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}
function writeMuted(m: boolean): void {
  try { localStorage.setItem(MUTE_KEY, m ? '1' : '0'); } catch { /* private mode */ }
}

// A short looping buffer of white noise — the raw material for pad shimmer,
// engine turbulence and the warp whoosh.
function makeNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;     // mute-controlled trunk
  private muted = readMuted();
  private started = false;

  // Engine hum chain.
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private engineOscA: OscillatorNode | null = null;
  private engineOscB: OscillatorNode | null = null;
  private engineNoiseGain: GainNode | null = null;
  private targetVel = 0;   // 0..1 normalised velocity
  private smoothVel = 0;
  private raf = 0;
  private lastFrame = 0;

  // Pad LFOs we keep references to so they can be torn down cleanly.
  private padNodes: AudioScheduledSourceNode[] = [];

  private noise: AudioBuffer | null = null;

  /** Build + start the whole audio graph. Safe to call repeatedly. */
  init(): void {
    if (this.started) { this.resume(); return; }
    const AC: AnyAC | undefined =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: AnyAC }).webkitAudioContext;
    if (!AC) return;

    let ctx: AudioContext;
    try { ctx = new AC(); } catch { return; }
    this.ctx = ctx;
    this.started = true;

    const master = ctx.createGain();
    master.gain.value = this.muted ? 0 : 1;
    master.connect(ctx.destination);
    this.master = master;

    this.noise = makeNoiseBuffer(ctx, 2.2);

    this.buildPad();
    this.buildEngine();

    // Listen for the global mute toggle.
    window.addEventListener('sw-toggle-mute', this.onToggleEvent);

    // Velocity smoothing / pad evolution loop.
    this.lastFrame = ctx.currentTime;
    const loop = () => {
      this.frame();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);

    this.resume();
  }

  /** Evolving atmospheric drone: stacked detuned saws through a slow filter,
   *  plus a breath of filtered noise — all panned and gently LFO-modulated. */
  private buildPad(): void {
    const ctx = this.ctx!;
    const out = ctx.createGain();
    out.gain.value = 0.16;
    out.connect(this.master!);

    // A warm low-pass that an LFO sweeps, so the timbre keeps shifting.
    const padFilter = ctx.createBiquadFilter();
    padFilter.type = 'lowpass';
    padFilter.frequency.value = 420;
    padFilter.Q.value = 6;
    padFilter.connect(out);

    const sweep = ctx.createOscillator();
    sweep.type = 'sine';
    sweep.frequency.value = 0.045;
    const sweepDepth = ctx.createGain();
    sweepDepth.gain.value = 240;
    sweep.connect(sweepDepth).connect(padFilter.frequency);
    sweep.start();
    this.padNodes.push(sweep);

    // A low triad (root, fifth, octave-ish) — deep and consonant.
    const freqs = [55, 82.41, 110, 164.81];
    for (let i = 0; i < freqs.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freqs[i];
      osc.detune.value = (i - 1.5) * 6; // slight chorus spread

      const vg = ctx.createGain();
      vg.gain.value = 0.5 / (i + 1);

      // Slow amplitude shimmer per voice so the pad "breathes".
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.05 + i * 0.017;
      const lfoDepth = ctx.createGain();
      lfoDepth.gain.value = vg.gain.value * 0.45;
      lfo.connect(lfoDepth).connect(vg.gain);
      lfo.start();

      osc.connect(vg).connect(padFilter);
      osc.start();
      this.padNodes.push(osc, lfo);
    }

    // A faint wash of filtered noise for "air".
    if (this.noise) {
      const air = ctx.createBufferSource();
      air.buffer = this.noise;
      air.loop = true;
      const airFilter = ctx.createBiquadFilter();
      airFilter.type = 'bandpass';
      airFilter.frequency.value = 600;
      airFilter.Q.value = 0.7;
      const airGain = ctx.createGain();
      airGain.gain.value = 0.05;
      air.connect(airFilter).connect(airGain).connect(out);
      air.start();
      this.padNodes.push(air);
    }
  }

  /** Engine hum: two detuned low oscillators + turbulence noise. Pitch, level
   *  and brightness all ride the normalised velocity in frame(). */
  private buildEngine(): void {
    const ctx = this.ctx!;
    const out = ctx.createGain();
    out.gain.value = 0.0; // rises with velocity
    out.connect(this.master!);
    this.engineGain = out;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 200;
    filter.Q.value = 1.2;
    filter.connect(out);
    this.engineFilter = filter;

    const a = ctx.createOscillator();
    a.type = 'sawtooth';
    a.frequency.value = 46;
    a.connect(filter);
    a.start();
    this.engineOscA = a;

    const b = ctx.createOscillator();
    b.type = 'square';
    b.frequency.value = 92;
    b.detune.value = 7;
    const bg = ctx.createGain();
    bg.gain.value = 0.5;
    b.connect(bg).connect(filter);
    b.start();
    this.engineOscB = b;

    // Turbulence: looping noise pushed through the same filter, scaled by speed.
    if (this.noise) {
      const n = ctx.createBufferSource();
      n.buffer = this.noise;
      n.loop = true;
      const ng = ctx.createGain();
      ng.gain.value = 0.0;
      n.connect(ng).connect(filter);
      n.start();
      this.engineNoiseGain = ng;
    }
  }

  /** Per-frame: smooth velocity toward its target and map it onto the engine. */
  private frame(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    const dt = Math.min(0.1, Math.max(0, now - this.lastFrame));
    this.lastFrame = now;

    // Critically-damped approach so changes feel smooth, not steppy.
    const k = 1 - Math.exp(-dt / 0.25);
    this.smoothVel += (this.targetVel - this.smoothVel) * k;
    const v = this.smoothVel;

    if (this.engineGain && this.engineFilter && this.engineOscA && this.engineOscB) {
      // Level: audible idle, swelling under thrust.
      const g = 0.06 + v * 0.22;
      this.engineGain.gain.setTargetAtTime(g, now, 0.05);
      // Pitch climbs ~an octave across the velocity range.
      const baseA = 42 + v * 38;
      this.engineOscA.frequency.setTargetAtTime(baseA, now, 0.08);
      this.engineOscB.frequency.setTargetAtTime(baseA * 2, now, 0.08);
      // Brightness opens up with speed.
      this.engineFilter.frequency.setTargetAtTime(160 + v * 1400, now, 0.08);
      if (this.engineNoiseGain) {
        this.engineNoiseGain.gain.setTargetAtTime(v * 0.10, now, 0.08);
      }
    }
  }

  /** Set current ship velocity in km/s; internally normalised to 0..1. */
  setVelocity(kms: number): void {
    if (!this.started) return;
    const norm = Math.max(0, Math.min(1, (kms || 0) / 75000));
    this.targetVel = norm;
  }

  /** Hyperdrive jump — a rising filtered-noise whoosh + a deep sub boom. */
  onWarp(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.noise) return;
    const t0 = ctx.currentTime;

    // Whoosh: noise through a band-pass that sweeps up then snaps down.
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 1.4;
    bp.frequency.setValueAtTime(300, t0);
    bp.frequency.exponentialRampToValueAtTime(5200, t0 + 0.42);
    bp.frequency.exponentialRampToValueAtTime(180, t0 + 1.1);
    const wg = ctx.createGain();
    wg.gain.setValueAtTime(0.0001, t0);
    wg.gain.exponentialRampToValueAtTime(0.5, t0 + 0.18);
    wg.gain.exponentialRampToValueAtTime(0.0008, t0 + 1.2);
    src.connect(bp).connect(wg).connect(this.master);
    src.start(t0);
    src.stop(t0 + 1.25);

    // Sub boom: a sine drop that you feel more than hear.
    const boom = ctx.createOscillator();
    boom.type = 'sine';
    boom.frequency.setValueAtTime(120, t0);
    boom.frequency.exponentialRampToValueAtTime(32, t0 + 0.9);
    const bgn = ctx.createGain();
    bgn.gain.setValueAtTime(0.0001, t0);
    bgn.gain.exponentialRampToValueAtTime(0.7, t0 + 0.06);
    bgn.gain.exponentialRampToValueAtTime(0.0008, t0 + 1.1);
    boom.connect(bgn).connect(this.master);
    boom.start(t0);
    boom.stop(t0 + 1.15);
  }

  /** Clean sonar-style ping (two-tone, with a short shimmer tail). */
  onScan(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t0 = ctx.currentTime;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.32, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0008, t0 + 0.7);
    g.connect(this.master);

    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(1320, t0);
    o.frequency.setValueAtTime(1760, t0 + 0.09);
    o.connect(g);
    o.start(t0);
    o.stop(t0 + 0.72);

    // A faint higher harmonic for sparkle.
    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.value = 2640;
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.0001, t0);
    g2.gain.exponentialRampToValueAtTime(0.08, t0 + 0.02);
    g2.gain.exponentialRampToValueAtTime(0.0006, t0 + 0.4);
    o2.connect(g2).connect(this.master);
    o2.start(t0);
    o2.stop(t0 + 0.42);
  }

  /** Soft UI blip for menu / button feedback. */
  onUi(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t0 = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(660, t0);
    o.frequency.exponentialRampToValueAtTime(880, t0 + 0.05);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0006, t0 + 0.16);
    o.connect(g).connect(this.master);
    o.start(t0);
    o.stop(t0 + 0.18);
  }

  /** Mute state (persisted). */
  isMuted(): boolean { return this.muted; }

  setMuted(m: boolean): void {
    this.muted = m;
    writeMuted(m);
    const ctx = this.ctx;
    if (this.master && ctx) {
      this.master.gain.setTargetAtTime(m ? 0 : 1, ctx.currentTime, 0.03);
    }
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    if (!this.muted) this.resume();
    return this.muted;
  }

  private onToggleEvent = (): void => { this.toggleMute(); };

  /** Resume a context the browser may have left/placed in 'suspended'. */
  private resume(): void {
    const ctx = this.ctx;
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  }

  /** Tear everything down (not required for the game, handy for tests/HMR). */
  dispose(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    window.removeEventListener('sw-toggle-mute', this.onToggleEvent);
    for (const n of this.padNodes) { try { n.stop(); } catch { /* already stopped */ } }
    this.padNodes = [];
    for (const o of [this.engineOscA, this.engineOscB]) { try { o?.stop(); } catch { /* noop */ } }
    if (this.ctx) void this.ctx.close();
    this.ctx = null;
    this.master = null;
    this.started = false;
  }
}

// Singleton — the game has exactly one soundscape.
export const audio = new AudioEngine();
export type { AudioEngine };
